import { useCombobox } from '@any-ui-react/core';
import classNames from 'classnames';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import {
  type ForwardedRef,
  forwardRef,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ComboboxWrapper } from './ComboboxWrapper';
import { findInData } from './group';
import type { ComboboxItemWithFormatted, SelectCommonProps, SelectDataProps } from './props';

export interface SelectSingleProps<TData extends ComboboxItemWithFormatted<TValue>, TValue extends string = string>
  extends SelectCommonProps {
  filter?: ((item: TValue) => boolean)[];
  value: string | null | undefined;
  onChange?: (option: TData | null) => void;
}

export const SelectSingle = forwardRef(
  <TData extends ComboboxItemWithFormatted>(
    {
      clearable = true,
      data,
      loading,
      value,
      onChange,
      onSearchChange,
      inputRef,
      ...props
    }: SelectSingleProps<TData> & SelectDataProps<TData>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const internalSearchRef = useRef<HTMLInputElement>(null);
    const searchRef = inputRef ?? internalSearchRef;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const [internalValue, setInternalValue] = useState<string | null | undefined>(value);
    const [searchValue, setSearchValue] = useState('');
    const combobox = useCombobox({
      onDropdownClose: () => {
        combobox.resetSelectedOption();
        setSearchValue('');
      },
    });

    const textValue = useMemo(() => {
      if (isEmpty(internalValue) || loading) {
        return '';
      }

      return findInData(data, internalValue as string)?.label ?? internalValue ?? '';
    }, [data, internalValue, loading]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: excludes `internalValue` to avoid overwriting user input when `handleValueSelect` is triggered
    useEffect(() => {
      if (!isEqual(value, internalValue)) {
        setInternalValue(value);
      }
    }, [value]);

    useEffect(() => {
      if (combobox.dropdownOpened && onSearchChange && searchRef.current) {
        searchRef.current.focus();
      }
    }, [combobox.dropdownOpened, onSearchChange, searchRef]);

    // Reads onChange via onChangeRef (not directly) so this callback doesn't need onChange in its deps
    const handleValueSelect = useCallback(
      (value: string) => {
        const option = findInData(data, value);
        setInternalValue(value);
        onChangeRef.current?.(option ?? null);
        combobox.closeDropdown();
      },
      [combobox, data],
    );

    const onClickRightSection = useCallback(() => {
      if (clearable && !isEmpty(internalValue)) {
        setInternalValue(null);
        onChangeRef.current?.(null);
      } else {
        combobox.toggleDropdown('mouse');
      }
    }, [clearable, combobox, internalValue]);

    return (
      <ComboboxWrapper
        {...props}
        clearable={clearable}
        combobox={combobox}
        data={data}
        handleValueSelect={handleValueSelect}
        internalValue={internalValue}
        isActive={it => internalValue === it}
        loading={loading}
        placeholder={combobox.dropdownOpened ? textValue || props.placeholder : props.placeholder}
        refOptions={ref}
        renderOption={item => (
          <span className={classNames('text-sm', { 'text-gray-6': item.disabled })}>
            {item.labelFormatted ?? item.label}
          </span>
        )}
        searchRef={searchRef}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        textValue={textValue}
        onClickRightSection={onClickRightSection}
        onSearchChange={onSearchChange}
      />
    );
  },
) as <TData extends ComboboxItemWithFormatted>(
  props: SelectSingleProps<TData> & {
    data: Array<TData>;
    loading: boolean;
    ref: ForwardedRef<HTMLDivElement>;
  },
) => ReactElement;
